import { useMemo, type CSSProperties } from 'react';
import DOMPurify from 'dompurify';
import styles from './EmailDescription.module.css';
import { layoutTicketDescription, type DescriptionLayoutClasses } from '../lib/ticketDescriptionLayout';

const LAYOUT_CLASSES: DescriptionLayoutClasses = {
  itil: styles.itil,
  row: styles.row,
  label: styles.label,
  body: styles.body,
  notes: styles.notes,
  note: styles.note,
};

interface EmailDescriptionProps {
  /** Roher Beschreibungs-/Mail-Inhalt (HTML oder Plain-Text). Wird sanitized. */
  html: string;
  /** Zusätzliche Klassen (z. B. aus dem aufrufenden Layout). */
  className?: string;
  style?: CSSProperties;
  /**
   * Inhalt ist sicher bekannter Klartext. Dann wird er als Text gesetzt statt als
   * HTML gedeutet — spitze Klammern im Nutzertext bleiben so erhalten.
   */
  plain?: boolean;
  /**
   * Ticket-Beschreibung: ITIL-Blöcke („Anliegen:", „Betroffen:" …) als Zeilen mit
   * Label-Spalte, Bearbeitungs-Hinweise als Fußzeile (`lib/ticketDescriptionLayout.ts`).
   * Nicht für Kommentare und Mails — dort ist „Ziel:" am Absatzanfang einfach Text.
   */
  ticketLayout?: boolean;
}

/**
 * Erkennt, ob die Mail eigene Text-/Hintergrundfarben mitbringt.
 * Solche Mails wurden für weißen Hintergrund gebaut und werden auf dem
 * Dark-Theme schwarz-auf-dunkel → sie kommen auf den hellen Briefbogen.
 */
function mailHasOwnColors(html: string): boolean {
  return (
    /style\s*=\s*["'][^"']*(?:color|background)/i.test(html) ||
    /<font[^>]+\bcolor\s*=/i.test(html) ||
    /\bbgcolor\s*=/i.test(html)
  );
}

/**
 * Grobe Heuristik: enthält der Inhalt überhaupt HTML-Tags?
 *
 * Nur ein Notbehelf für Quellen, die ihr Format nicht mitliefern. Wer es weiß, soll
 * `plain` setzen — die Heuristik hält „Fehler beim Öffnen von <Datei>" für HTML und
 * lässt den Sanitizer den vermeintlichen Tag entfernen. Der Nutzer verliert dann
 * einen Teil seines eigenen Textes.
 */
function looksLikeHtml(html: string): boolean {
  return /<[a-z!/][^>]*>/i.test(html);
}

/**
 * Einheitliche, dark-theme-taugliche Darstellung von Ticket-Beschreibungen,
 * die aus E-Mails stammen. Hybrid: Dark-Look als Standard, automatischer
 * heller Briefbogen für Mails mit eigenen Farben (sonst unlesbar).
 */
export default function EmailDescription({
  html,
  className,
  style,
  plain: forcePlain,
  ticketLayout,
}: EmailDescriptionProps) {
  const { clean, light, plain } = useMemo(() => {
    // Bekannter Klartext wird gar nicht erst als HTML behandelt: React setzt ihn als
    // Textknoten, damit bleiben spitze Klammern erhalten statt als Tag zu verschwinden.
    if (forcePlain) {
      return { clean: null, light: false, plain: true };
    }
    const clean = DOMPurify.sanitize(html);
    const light = mailHasOwnColors(clean);
    const plain = !looksLikeHtml(clean);
    // Gegliedert wird nur, was ohnehin im Dark-Look steht: Eine Mail mit eigenen Farben
    // ist kein KIAN-Text, und die Label-Spalte passte nicht auf den hellen Briefbogen.
    const laidOut = ticketLayout && !light && !plain ? layoutTicketDescription(clean, LAYOUT_CLASSES) : null;
    return { clean: laidOut ?? clean, light, plain };
  }, [html, forcePlain, ticketLayout]);

  /**
   * React 19 vergleicht Props per Identität: ein frisch erzeugtes
   * `{ __html }`-Objekt lässt React bei JEDEM Re-Render das komplette innerHTML
   * neu schreiben — alle Kindknoten werden ersetzt, Bilder laden neu und die
   * Höhe springt. Bei langen Mail-Beschreibungen sprang deshalb der halbe
   * Bildschirm, sobald irgendwo im Cockpit getippt wurde. Objekt memoisieren.
   */
  const innerHtml = useMemo(() => (clean === null ? null : { __html: clean }), [clean]);

  const classes = [
    styles.content,
    light ? styles.light : styles.dark,
    plain ? styles.plain : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (clean === null) {
    return (
      <div className={classes} style={style}>
        {html}
      </div>
    );
  }

  return (
    <div
      className={classes}
      style={style}
      dangerouslySetInnerHTML={innerHtml ?? undefined}
    />
  );
}
